import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { PaymentControllerService } from '../../../../api/payment-service/api/paymentController.service';
import { PaymentSearchParamsDTO } from '../../../../api/payment-service/model/models';
import {
  PaymentHistory,
  PaymentHistorySearchParams,
} from './payment-history.config';

@Injectable({
  providedIn: 'root',
})
export class PaymentHistoryService {
  constructor(
    private readonly paymentControllerService: PaymentControllerService
  ) {}

  getPaymentHistory(
    params?: PaymentHistorySearchParams
  ): Observable<{ data: PaymentHistory[]; total: number }> {
    // Convert frontend params to API DTO
    const searchParams: PaymentSearchParamsDTO = {
      orderCode: params?.orderCode,
      status: params?.status as any,
      pageRequest: {
        page: params?.pageRequest?.page || 0,
        size: params?.pageRequest?.size || 20,
        sortBy: params?.pageRequest?.sortBy || 'creationDate',
        ascending: params?.pageRequest?.ascending || false,
      },
    };

    return this.paymentControllerService
      .searchPayments(searchParams, 'response', false, {
        httpHeaderAccept: '*/*',
      })
      .pipe(
        switchMap(
          (
            response: any
          ): Observable<{ data: PaymentHistory[]; total: number }> => {
            // Handle both blob and JSON responses
            if (response.body instanceof Blob) {
              // Convert blob to text and parse as JSON
              return new Observable<{ data: PaymentHistory[]; total: number }>(
                (observer) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    try {
                      const jsonData = JSON.parse(reader.result as string);
                      observer.next({
                        data: jsonData.pageContent || [],
                        total: jsonData.totalElements || 0,
                      });
                      observer.complete();
                    } catch (error) {
                      console.error('Error parsing blob response:', error);
                      observer.next({ data: [], total: 0 });
                      observer.complete();
                    }
                  };
                  reader.onerror = () => {
                    observer.next({ data: [], total: 0 });
                    observer.complete();
                  };
                  reader.readAsText(response.body);
                }
              );
            } else {
              // If response is already JSON
              return of({
                data: response.body?.pageContent || [],
                total: response.body?.totalElements || 0,
              });
            }
          }
        ),
        catchError((error) => {
          console.error('Error fetching payment history:', error);
          // Return empty result on error
          return of({ data: [], total: 0 });
        })
      );
  }

  getPaymentHistoryById(id: number): Observable<PaymentHistory | null> {
    return this.paymentControllerService.getPayment(id).pipe(
      map((response) => response as PaymentHistory),
      catchError((error) => {
        console.error('Error fetching payment details:', error);
        return of(null);
      })
    );
  }

  // Placeholder methods for future API integration
  processRefund(
    paymentId: number,
    refundAmount: number,
    refundReason: string
  ): Observable<boolean> {
    return of(true).pipe(delay(500));
  }

  retryPayment(paymentId: number): Observable<boolean> {
    return of(true).pipe(delay(500));
  }

  cancelPayment(paymentId: number, reason: string): Observable<boolean> {
    return of(true).pipe(delay(500));
  }
}
